/**
 * @file 01-coaching-identity.js — IMGA+ 1:1 coaching identity capture (task #1097)
 *
 * Location: Shopify Theme > Assets > 01-coaching-identity.js
 * Loaded:   sitewide from layout/theme.liquid (after jquery.cookie.js)
 *
 * WHAT THIS SOLVES
 * ────────────────
 * The mobile app's "Buy more sessions" button sends a shopper to the coaching
 * collection with their Auth0 identity on the query string:
 *
 *   /collections/coaching?auth0_id=auth0|abc123&share_with=auth0|p1,auth0|p2
 *
 *   auth0_id   — the primary user (the student) whose platform access this
 *                purchase grants.
 *   share_with — additional Auth0 IDs (typically the parents) who should be
 *                granted access to the same purchase. Zero or more, comma
 *                separated. May be absent.
 *
 * That identity has to survive the shopper browsing (collection → product →
 * cart → checkout) and end up on the ORDER, because our webhook relay forwards
 * it to NCSA and the order is the only thing the webhook sees. The query string
 * is gone the moment they click a product, so it is captured on arrival,
 * persisted for the session, and re-applied on every page.
 *
 * WHY THIS REPLACES THE ROUTER CALLBACK (context, not a change of behaviour)
 * ─────────────────────────────────────────────────────────────────────────
 * Historically NCSA deep-linked into /pages/router with a variant ID and we
 * added to cart for them (router.js). They asked to move to a browse-and-buy
 * collection page because keeping our variant IDs in sync on their side kept
 * breaking. Nothing about router.js changes — it stays for the existing
 * integrations. This file is the same idea for the new entry point: capture
 * identity from the URL, put it on the cart.
 *
 * WHERE THE DATA IS WRITTEN — three places, deliberately
 * ─────────────────────────────────────────────────────
 * 1. Cart attributes `_auth0_user_id` and `_share_with`. This is the data
 *    contract NCSA specified. Cart attributes land on the order as
 *    `note_attributes`, which is what the webhook relay reads. Order level, so
 *    it survives no matter which line items are in the cart.
 *
 * 2. The existing `customer` cart attribute, which already carries `auth0_id`
 *    when a shopper arrives through router.js (see router.js
 *    buildCustomerProperties). Merging into it means every downstream reader
 *    that already understands the router's shape — checkout.liquid, cart.js,
 *    share-cart, the CRM order sync — keeps working with no backend change.
 *
 * 3. Line item properties `_auth0_user_id` / `_share_with`, injected as hidden
 *    inputs into every add-to-cart form. Courtney asked for line-item level
 *    specifically, and it is the durable fallback: an order can lose a cart
 *    attribute if something later overwrites the cart, but a line item keeps
 *    the properties it was added with. The leading underscore is the Shopify
 *    convention for a property that must not be displayed to the shopper at
 *    checkout, matching router.js buildLineItemProperties.
 *
 * MERGE, NEVER REPLACE
 * ────────────────────
 * The cart's `customer` attribute is shared with rep attribution
 * (01-rep-functionality.js) and the camp flows. It is always read back and
 * merged, never written blind — the same rule add-to-cart.js follows. We also
 * never write the strings "null"/"undefined", which downstream readers
 * JSON.parse back to null and crash on (see the guard in cart.js).
 *
 * NOT IN SCOPE / KNOWN LIMITS
 * ───────────────────────────
 * - The exact share_with shape NCSA wants on the webhook relay is not final.
 *   We store the normalised comma-separated string; reshaping it for the relay
 *   is a backend change, not a theme change.
 * - The page cannot prove the browser belongs to the person named in
 *   auth0_id (a forwarded or bookmarked link carries someone else's ID). The
 *   spec records this as an accepted risk.
 * - Missing or empty parameters are not an error. The page must stay fully
 *   shoppable, and the shopper simply falls through to the existing
 *   activation-email path on ecom's side.
 *
 * The pure functions at the top are exported for unit tests
 * (tests/coaching-identity.test.js) and are the only place the parsing and
 * property-assignment rules live.
 */

(function (root, factory) {
  'use strict';

  var api = factory();

  // Unit tests require this file directly; the browser gets the side effects.
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.IMGCoachingIdentity = api;
  api.init();
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ──────────────────────────────────────────────────────────────────────────
  // CONFIGURATION
  // ──────────────────────────────────────────────────────────────────────────

  var CONFIG = {
    // Query string parameters the mobile app sends.
    paramAuth0: 'auth0_id',
    paramShareWith: 'share_with',

    // Cart attribute / line item property names — NCSA's data contract.
    attrAuth0: '_auth0_user_id',
    attrShareWith: '_share_with',

    // Session cookies. Named like the theme's other identity cookies
    // (_img_rep, _img_rep_data) so they are recognisable in dev tools.
    cookieAuth0: '_img_auth0_id',
    cookieShareWith: '_img_share_with',
    // Records which identity has already been written to the cart, so a
    // shopper browsing ten products does not trigger ten cart writes.
    cookieSynced: '_img_auth0_synced',

    cartGetUrl: '/cart.js',
    cartUpdateUrl: '/cart/update.js',

    // Defensive caps. Shopify accepts long attribute values, but an
    // unbounded value from a URL is somebody else's bug waiting to become
    // ours — a truncated ID is easier to diagnose than a rejected cart write.
    maxValueLength: 2000,
    maxShareWithIds: 50,

    addToCartFormSelector: 'form[action^="/cart/add"]'
  };

  // ──────────────────────────────────────────────────────────────────────────
  // PURE HELPERS — parsing and property assignment (unit tested)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Read a single parameter out of a query string.
   *
   * Written against the raw query string rather than URLSearchParams because
   * this file is served to the browser verbatim (src/assets/* is not compiled
   * or polyfilled by Slate) and because it makes the function trivially
   * testable with a plain string. Matches the urlQuery() helper the theme's
   * other legacy assets each carry their own copy of.
   *
   * @param {string} search - e.g. "?auth0_id=auth0%7Cabc&share_with=x,y"
   * @param {string} name   - parameter name
   * @returns {string} decoded value, or "" when absent/blank/undecodable
   */
  function getParam(search, name) {
    if (typeof search !== 'string' || !search || typeof name !== 'string') {
      return '';
    }

    var query = search.charAt(0) === '?' ? search.slice(1) : search;
    var pairs = query.split('&');

    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      var key = eq === -1 ? pairs[i] : pairs[i].slice(0, eq);

      if (decodeMaybe(key) !== name) {
        continue;
      }

      var raw = eq === -1 ? '' : pairs[i].slice(eq + 1);
      // A "+" is a space in a query string; decodeURIComponent does not know
      // that. Auth0 IDs do not contain spaces, but a malformed link should
      // still decode to something sane rather than throw.
      return decodeMaybe(raw.replace(/\+/g, ' ')).trim();
    }

    return '';
  }

  /** decodeURIComponent that returns the input instead of throwing on "%zz". */
  function decodeMaybe(value) {
    try {
      return decodeURIComponent(value);
    } catch (e) {
      return value;
    }
  }

  /**
   * True when a value is worth persisting.
   *
   * Rejects the literal strings "null"/"undefined", which arrive whenever a
   * caller string-interpolates a missing value into the link. Writing those to
   * the cart is how downstream readers end up JSON.parsing a null and crashing
   * (the same guard cart.js applies to its attributes).
   */
  function isUsableValue(value) {
    if (typeof value !== 'string') {
      return false;
    }

    var trimmed = value.trim();

    return trimmed !== '' &&
      trimmed !== 'null' &&
      trimmed !== 'undefined' &&
      trimmed !== 'NaN';
  }

  /**
   * Normalise share_with into a JSON-array string — the shape ecom's
   * ShareWithApplier requires (LineItem.ShareWith() / apply_share_with.go).
   * A comma-separated string is a permanent provisioning failure
   * (reason:malformed_share_with); this used to emit exactly that.
   *
   * Two input shapes are accepted because two were specified: the ticket's
   * example link uses a bare comma-separated list (share_with=id,id,id)
   * while the written spec describes a URL-encoded JSON array
   * (["auth0|abc"]). Both normalise to the same JSON-array-string output so
   * the value on the order is one predictable shape whatever NCSA send.
   *
   * Blank entries are dropped and duplicates collapsed, so a trailing comma or
   * a repeated parent does not reach the order.
   *
   * @param {string} raw
   * @returns {string} "" when there is nothing usable
   */
  function normalizeShareWith(raw) {
    if (!isUsableValue(raw)) {
      return '';
    }

    var value = raw.trim();
    var parts;

    if (value.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(value);
        parts = Array.isArray(parsed) ? parsed : [value];
      } catch (e) {
        // Looked like JSON, was not. Fall back to comma splitting rather than
        // dropping an identity we can still read.
        parts = value.split(',');
      }
    } else {
      parts = value.split(',');
    }

    var seen = {};
    var ids = [];

    for (var i = 0; i < parts.length; i++) {
      var id = typeof parts[i] === 'string' ? parts[i].trim() : '';

      if (!isUsableValue(id) || Object.prototype.hasOwnProperty.call(seen, id)) {
        continue;
      }

      seen[id] = true;
      ids.push(id);

      if (ids.length >= CONFIG.maxShareWithIds) {
        break;
      }
    }

    return ids.length ? JSON.stringify(ids) : '';
  }

  /** Trim a value to the configured cap without throwing on a non-string. */
  function capValue(value) {
    return typeof value === 'string' ? value.slice(0, CONFIG.maxValueLength) : '';
  }

  /**
   * Pull the identity out of a query string.
   *
   * @param {string} search - window.location.search
   * @returns {{auth0Id: string, shareWith: string}} empty strings when absent
   */
  function parseIdentityParams(search) {
    var auth0Id = getParam(search, CONFIG.paramAuth0);
    var shareWith = getParam(search, CONFIG.paramShareWith);

    return {
      auth0Id: isUsableValue(auth0Id) ? capValue(auth0Id.trim()) : '',
      shareWith: capValue(normalizeShareWith(shareWith))
    };
  }

  /** True when there is anything worth storing. */
  function hasIdentity(identity) {
    return Boolean(identity && (identity.auth0Id || identity.shareWith));
  }

  /**
   * Line item properties for an identity.
   *
   * Underscore-prefixed so checkout does not display them to the shopper,
   * matching router.js buildLineItemProperties.
   *
   * @param {{auth0Id: string, shareWith: string}} identity
   * @returns {Object} may be empty
   */
  function buildLineItemProperties(identity) {
    var properties = {};

    if (!identity) {
      return properties;
    }

    if (isUsableValue(identity.auth0Id)) {
      properties[CONFIG.attrAuth0] = identity.auth0Id;
    }

    // share_with is only meaningful alongside a primary user, and an order
    // carrying shares for nobody would be a puzzle for whoever reads it.
    if (isUsableValue(identity.auth0Id) && isUsableValue(identity.shareWith)) {
      properties[CONFIG.attrShareWith] = identity.shareWith;
    }

    return properties;
  }

  /**
   * Build the cart attributes to POST, merged onto what the cart already has.
   *
   * Returns null when there is nothing to change, so the caller can skip the
   * request entirely rather than writing the cart back to itself.
   *
   * @param {{auth0Id: string, shareWith: string}} identity
   * @param {Object} existingAttributes - cart.attributes from /cart.js
   * @returns {Object|null} the full attributes object to send
   */
  function buildCartAttributes(identity, existingAttributes) {
    if (!hasIdentity(identity)) {
      return null;
    }

    var attributes = {};
    var key;

    // Start from what is already on the cart. Anything we do not understand
    // (participants, cToken, another team's attribute) has to survive us.
    if (existingAttributes && typeof existingAttributes === 'object') {
      for (key in existingAttributes) {
        if (Object.prototype.hasOwnProperty.call(existingAttributes, key)) {
          attributes[key] = existingAttributes[key];
        }
      }
    }

    var before = JSON.stringify(attributes);

    if (isUsableValue(identity.auth0Id)) {
      attributes[CONFIG.attrAuth0] = identity.auth0Id;
    }

    if (isUsableValue(identity.shareWith)) {
      attributes[CONFIG.attrShareWith] = identity.shareWith;
    }

    // Fold the same values into the shared `customer` attribute so every
    // existing consumer of the router's shape keeps working untouched.
    attributes.customer = JSON.stringify(
      mergeCustomerAttribute(attributes.customer, identity)
    );

    return JSON.stringify(attributes) === before ? null : attributes;
  }

  /**
   * Merge the identity into the cart's `customer` JSON attribute.
   *
   * `customer` is shared state — rep attribution, camper details and the camp
   * selector all write to it — so it is parsed, added to, and handed back.
   * A value that is missing or unparseable is treated as an empty object
   * rather than a reason to fail: losing rep attribution would be worse than
   * the malformed value we found.
   *
   * @param {string|Object} existingCustomer
   * @param {{auth0Id: string, shareWith: string}} identity
   * @returns {Object}
   */
  function mergeCustomerAttribute(existingCustomer, identity) {
    var customer = {};

    if (existingCustomer && typeof existingCustomer === 'object') {
      customer = existingCustomer;
    } else if (typeof existingCustomer === 'string' && existingCustomer.trim() !== '') {
      try {
        var parsed = JSON.parse(existingCustomer);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          customer = parsed;
        }
      } catch (e) {
        // Keep the empty object; see the doc comment.
      }
    }

    if (identity && isUsableValue(identity.auth0Id)) {
      // `auth0_id` (no underscore) is the key router.js already writes here.
      customer.auth0_id = identity.auth0Id;
    }

    if (identity && isUsableValue(identity.shareWith)) {
      customer.share_with = identity.shareWith;
    }

    return customer;
  }

  /** A stable string identifying an identity, used to skip repeat writes. */
  function identityFingerprint(identity) {
    if (!hasIdentity(identity)) {
      return '';
    }

    return identity.auth0Id + '|' + identity.shareWith;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // BROWSER SIDE — storage, cart write, form injection
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Session cookie read/write.
   *
   * Uses jquery.cookie ($.cookie) when it is loaded, matching
   * 01-rep-functionality.js, and falls back to document.cookie so this file
   * still works if it is ever loaded before the plugin. No expiry is set: the
   * identity is meant to last the browser session, not outlive it.
   */
  function readCookie(name) {
    if (typeof window !== 'undefined' && window.jQuery && window.jQuery.cookie) {
      return window.jQuery.cookie(name) || '';
    }

    if (typeof document === 'undefined' || !document.cookie) {
      return '';
    }

    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
    );

    return match ? decodeMaybe(match[1]) : '';
  }

  function writeCookie(name, value) {
    if (typeof window !== 'undefined' && window.jQuery && window.jQuery.cookie) {
      window.jQuery.cookie(name, value, { path: '/' });
      return;
    }

    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/; SameSite=Lax' +
      (typeof window !== 'undefined' && window.location &&
        window.location.protocol === 'https:' ? '; Secure' : '');
  }

  /** Persist a captured identity for the rest of the session. */
  function storeIdentity(identity) {
    if (isUsableValue(identity.auth0Id)) {
      writeCookie(CONFIG.cookieAuth0, identity.auth0Id);
    }

    if (isUsableValue(identity.shareWith)) {
      writeCookie(CONFIG.cookieShareWith, identity.shareWith);
    }
  }

  /** The identity stored earlier in this session, if any. */
  function loadStoredIdentity() {
    return {
      auth0Id: capValue((readCookie(CONFIG.cookieAuth0) || '').trim()),
      shareWith: capValue(normalizeShareWith(readCookie(CONFIG.cookieShareWith) || ''))
    };
  }

  /**
   * Resolve the identity for this page view.
   *
   * The URL always wins: a shopper who follows a fresh link from the app is
   * telling us who they are now, which may not be who they were an hour ago on
   * a shared device.
   */
  function resolveIdentity(search) {
    var fromUrl = parseIdentityParams(search);

    if (hasIdentity(fromUrl)) {
      storeIdentity(fromUrl);
      return fromUrl;
    }

    return loadStoredIdentity();
  }

  /**
   * Put the identity on the cart.
   *
   * Reads the cart first so the write is a merge (see buildCartAttributes),
   * and records the fingerprint afterwards so browsing more products does not
   * repeat the write. A failure here is logged and swallowed: the line item
   * properties are already attached, and no cart problem should stop somebody
   * buying.
   */
  function syncCart(identity) {
    if (!hasIdentity(identity) || typeof window === 'undefined' || !window.jQuery) {
      return;
    }

    var fingerprint = identityFingerprint(identity);

    if (readCookie(CONFIG.cookieSynced) === fingerprint) {
      return;
    }

    var $ = window.jQuery;

    $.ajax({ type: 'GET', url: CONFIG.cartGetUrl, dataType: 'json' })
      .done(function (cart) {
        var attributes = buildCartAttributes(identity, cart && cart.attributes);

        if (!attributes) {
          writeCookie(CONFIG.cookieSynced, fingerprint);
          return;
        }

        $.ajax({
          type: 'POST',
          url: CONFIG.cartUpdateUrl,
          dataType: 'json',
          data: { attributes: attributes }
        })
          .done(function () {
            writeCookie(CONFIG.cookieSynced, fingerprint);
          })
          .fail(function (xhr) {
            console.log('[coaching-identity] cart attribute write failed', xhr && xhr.status);
          });
      })
      .fail(function (xhr) {
        console.log('[coaching-identity] could not read cart', xhr && xhr.status);
      });
  }

  /**
   * Add hidden identity inputs to every add-to-cart form on the page.
   *
   * Covers both the standard product form and the theme's ajaxified add to
   * cart, which serialises the same form. Idempotent — re-running updates the
   * existing inputs rather than adding a second copy, so it is safe to call
   * again after new markup appears.
   */
  function decorateForms(identity, doc) {
    var target = doc || (typeof document !== 'undefined' ? document : null);

    if (!target || !hasIdentity(identity)) {
      return 0;
    }

    var properties = buildLineItemProperties(identity);
    var names = Object.keys(properties);

    if (names.length === 0) {
      return 0;
    }

    var forms = target.querySelectorAll(CONFIG.addToCartFormSelector);
    var decorated = 0;

    for (var i = 0; i < forms.length; i++) {
      for (var n = 0; n < names.length; n++) {
        var inputName = 'properties[' + names[n] + ']';
        var input = forms[i].querySelector('input[name="' + inputName + '"]');

        if (!input) {
          input = target.createElement('input');
          input.type = 'hidden';
          input.name = inputName;
          input.setAttribute('data-coaching-identity', '');
          forms[i].appendChild(input);
        }

        input.value = properties[names[n]];
      }

      decorated++;
    }

    return decorated;
  }

  /**
   * Entry point. Safe to run on every page: without a parameter and without a
   * stored identity it reads two cookies, finds nothing, and stops.
   */
  function init() {
    if (typeof window === 'undefined' || !window.location) {
      return;
    }

    var identity = resolveIdentity(window.location.search);

    if (!hasIdentity(identity)) {
      return;
    }

    syncCart(identity);
    decorateForms(identity);

    // Product forms on this theme can be re-rendered after load (variant
    // pickers, quick-add). Re-apply once the DOM is ready so a form that
    // appeared late still carries the properties.
    if (typeof document !== 'undefined' && document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        decorateForms(identity);
      });
    }
  }

  return {
    // Exported for tests.
    getParam: getParam,
    isUsableValue: isUsableValue,
    normalizeShareWith: normalizeShareWith,
    parseIdentityParams: parseIdentityParams,
    hasIdentity: hasIdentity,
    buildLineItemProperties: buildLineItemProperties,
    buildCartAttributes: buildCartAttributes,
    mergeCustomerAttribute: mergeCustomerAttribute,
    identityFingerprint: identityFingerprint,
    decorateForms: decorateForms,
    resolveIdentity: resolveIdentity,
    CONFIG: CONFIG,

    // Called by the browser bootstrap above.
    init: init
  };
});
